import React, { useRef, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { Modal, Descriptions, Tag, Button, Table, Divider } from 'antd';
import { SUBSCRIBER_TYPE_LABELS } from '../../models/types';
import { useApp } from '../../context/AppContext';
import { formatCurrency } from '../../utils/formatters';

const SubscriberDetails = ({ visible, onClose, subscriber }) => {
  const detailRef = useRef();
  const [appName, setAppName] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [meters, setMeters] = useState([]);

  useEffect(() => {
    import('../../services/storageService').then(async ({ default: storageService }) => {
      setAppName(await storageService.getSetting('app_name', 'Gestion EAU'));
      if (subscriber?.id) {
        const [invs, mets] = await Promise.all([
          storageService.getInvoicesBySubscriber(subscriber.id),
          storageService.getMetersBySubscriber(subscriber.id)
        ]);
        setInvoices(invs);
        setMeters(mets);
      } else {
        setInvoices([]);
        setMeters([]);
      }
    });
  }, [subscriber]);

  if (!subscriber) return null;


  const invoiceColumns = [
    {
      title: 'Numéro',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber'
    },
    {
      title: 'Montant',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Payé',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Reste',
      dataIndex: 'remainingAmount',
      key: 'remainingAmount',
      render: (amount) => formatCurrency(amount)
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status'
    }
  ];

  // Export PDF détail abonné + factures
  const handleExportPDF = async () => {
    const node = detailRef.current.cloneNode(true);
    // Supprime les colonnes d'action s'il y en a (pour factures)
    node.querySelectorAll('th').forEach((th, idx) => {
      if (th.innerText.toLowerCase().includes('action')) {
        th.parentNode.removeChild(th);
        node.querySelectorAll('tr').forEach(tr => {
          if (tr.children[idx]) tr.removeChild(tr.children[idx]);
        });
      }
    });
    // Ajoute le nom de l'appli en haut
    const wrapper = document.createElement('div');
    wrapper.style.padding = '24px';
    const appTitle = document.createElement('h2');
    appTitle.innerText = appName || 'Gestion EAU';
    appTitle.style.textAlign = 'center';
    appTitle.style.marginBottom = '8px';
    const tableTitle = document.createElement('h3');
    tableTitle.innerText = `Fiche abonné & factures`;
    tableTitle.style.textAlign = 'center';
    tableTitle.style.marginBottom = '16px';
    wrapper.appendChild(appTitle);
    wrapper.appendChild(tableTitle);
    wrapper.appendChild(node);
    document.body.appendChild(wrapper);
    const canvas = await html2canvas(wrapper, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pageWidth / imgWidth, pageHeight / imgHeight);
    const x = (pageWidth - imgWidth * ratio) / 2;
    const y = 20;
    pdf.addImage(imgData, 'PNG', x, y, imgWidth * ratio, imgHeight * ratio);
    pdf.save(`fiche_abonne_${subscriber.lastName}.pdf`);
    document.body.removeChild(wrapper);
  };

  return (
    <Modal
      title={`Détails de ${subscriber.firstName} ${subscriber.lastName}`}
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="export" onClick={handleExportPDF} type="primary">Exporter PDF</Button>,
        <Button key="close" onClick={onClose}>Fermer</Button>
      ]}
    >
      <div ref={detailRef}>
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Nom complet">
            {subscriber.firstName} {subscriber.lastName}
          </Descriptions.Item>
          <Descriptions.Item label="Type d'abonné">
            <Tag color="blue">{SUBSCRIBER_TYPE_LABELS[subscriber.type]}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="CIN">
            {subscriber.cin}
          </Descriptions.Item>
          <Descriptions.Item label="Téléphone">
            {subscriber.phone}
          </Descriptions.Item>
          <Descriptions.Item label="Adresse" span={2}>
            {subscriber.address}, {subscriber.city}
          </Descriptions.Item>
          <Descriptions.Item label="Caution">
            {subscriber.caution} FCFA
          </Descriptions.Item>
          <Descriptions.Item label="Caution payée">
            <Tag color={subscriber.cautionPaid ? 'green' : 'red'}>
              {subscriber.cautionPaid ? 'Oui' : 'Non'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Date d'enregistrement">
            {new Date(subscriber.registrationDate).toLocaleDateString('fr-FR')}
          </Descriptions.Item>
          <Descriptions.Item label="Statut">
            <Tag color={subscriber.status === 'active' ? 'green' : 'red'}>
              {subscriber.status === 'active' ? 'Actif' : 'Inactif'}
            </Tag>
          </Descriptions.Item>
        </Descriptions>

        {subscriber.notes && (
          <>
            <Divider />
            <h4>Notes</h4>
            <p>{subscriber.notes}</p>
          </>
        )}

        <Divider>Compteurs</Divider>
        <p>Nombre de compteurs: <strong>{meters.length}</strong></p>
        {meters.length > 0 && (
          <Table
            columns={[
              { title: 'N° Série', dataIndex: 'serialNumber', key: 'serialNumber' },
              { title: 'Marque', dataIndex: 'brand', key: 'brand' },
              { title: 'Modèle', dataIndex: 'model', key: 'model' },
              { title: 'Statut', dataIndex: 'status', key: 'status' }
            ]}
            dataSource={meters}
            rowKey="id"
            pagination={false}
            size="small"
          />
        )}

        <Divider>Factures</Divider>
        <Table
          columns={invoiceColumns}
          dataSource={invoices}
          rowKey="id"
          pagination={{ pageSize: 5 }}
          size="small"
        />
      </div>
    </Modal>
  );
};

export default SubscriberDetails;
